import React, { useEffect, useState, useCallback } from "react";
import axios from "../../api/axiosAuth";
import { useAuth } from "../../context/AuthContext";
import { API as BASE_API } from "../config"; // ✅ Configuración centralizada
import "../../style/buscarEstudianteSecretaria.css";

const API_BASE = `${BASE_API}`;

export default function SecretariaBuscarEstudiantes() {
  const { token, user } = useAuth();

  /* ==========================
      📦 ESTADO DE DATOS
  ========================== */
  const [estudiantes, setEstudiantes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 15;

  /* ==========================
      🔍 FILTROS
  ========================== */
  const [filtros, setFiltros] = useState({
    sede_id: user?.sede_id || "",
    grado: "",
    grupo: "",
    estado: "activo",
    busqueda: ""
  });

  /* ==========================
      📄 FICHA / EDICIÓN
  ========================== */
  const [mostrarFicha, setMostrarFicha] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [ficha, setFicha] = useState(null);
  const [loadingFicha, setLoadingFicha] = useState(false);
  const [guardando, setGuardando] = useState(false);

  /* ==========================
      📦 CARGAR ESTUDIANTES
  ========================== */
  const cargarEstudiantes = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const params = {
        page,
        limit,
        sede_id: filtros.sede_id || undefined,
        grado: filtros.grado || undefined,
        grupo: filtros.grupo || undefined,
        estado: filtros.estado || undefined,
        nombre: filtros.busqueda || undefined,
      };

      const res = await axios.get(`${API_BASE}/secretaria/estudiantes/buscar`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      setEstudiantes(res.data.results || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error("❌ Error cargando estudiantes:", err);
    } finally {
      setLoading(false);
    }
  }, [token, page, filtros]);

  useEffect(() => {
    cargarEstudiantes();
  }, [cargarEstudiantes]);

  const handleFiltroChange = (e) => {
    setFiltros(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setPage(1); // Reiniciar a página 1 al filtrar
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  /* ==========================
      📄 LÓGICA DE FICHA
  ========================= */
  const abrirFicha = async (id) => {
    try {
      setLoadingFicha(true);
      setMostrarFicha(true);
      const res = await axios.get(`${API_BASE}/secretaria/estudiantes/ficha/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFicha(res.data);
    } catch (err) {
      console.error("❌ Error ficha:", err);
      setMostrarFicha(false);
    } finally {
      setLoadingFicha(false);
    }
  };

  const guardarFicha = async () => {
    try {
      setGuardando(true);
      await axios.put(`${API_BASE}/secretaria/estudiantes/${ficha.id}`, ficha, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert("✅ Datos actualizados correctamente");
      setModoEdicion(false);
      cargarEstudiantes();
    } catch (err) {
      console.error("❌ Error al guardar:", err);
      alert("No se pudieron guardar los cambios.");
    } finally {
      setGuardando(false);
    }
  };

  const imprimirPDF = async () => {
    try {
      const res = await axios.get(`${API_BASE}/secretaria/estudiantes/${ficha.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Ficha_${ficha.documento_estudiante}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("❌ Error PDF:", err);
    }
  };

  return (
    <div className="secretaria-buscar-container p-6">
      <h2 className="text-2xl font-bold mb-6">🔍 Buscador Maestro de Estudiantes</h2>

      {/* FILTROS */}
      <div className="filtros-grid bg-white p-4 rounded-xl shadow-sm border mb-6 grid grid-cols-1 md:grid-cols-5 gap-4">
        <input
          name="busqueda"
          placeholder="Nombre o Documento..."
          value={filtros.busqueda}
          onChange={handleFiltroChange}
          className="p-2 border rounded-lg outline-none focus:ring-2 focus:ring-blue-400"
        />
        <select name="sede_id" value={filtros.sede_id} onChange={handleFiltroChange} className="p-2 border rounded-lg">
          <option value="">Todas las sedes</option>
          {user?.sedes?.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
        </select>
        <select name="grado" value={filtros.grado} onChange={handleFiltroChange} className="p-2 border rounded-lg">
          <option value="">Grado</option>
          {[...Array(11)].map((_, i) => <option key={i+1} value={i+1}>{i+1}°</option>)}
        </select>
        <select name="estado" value={filtros.estado} onChange={handleFiltroChange} className="p-2 border rounded-lg">
          <option value="activo">Activos</option>
          <option value="retirado">Retirados</option>
          <option value="expulsado">Expulsados</option>
        </select>
        <button onClick={cargarEstudiantes} className="bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700">
          Refrescar
        </button>
      </div>

      {/* TABLA */}
      <div className="bg-white rounded-xl shadow-lg border overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="p-4 text-xs font-bold uppercase text-gray-500">Documento</th>
              <th className="p-4 text-xs font-bold uppercase text-gray-500">Nombre Completo</th>
              <th className="p-4 text-xs font-bold uppercase text-gray-500">Grado/Grupo</th>
              <th className="p-4 text-xs font-bold uppercase text-gray-500">Sede</th>
              <th className="p-4 text-xs font-bold uppercase text-gray-500">Estado</th>
              <th className="p-4 text-xs font-bold uppercase text-gray-500 text-center">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan="6" className="p-10 text-center text-gray-400 italic">Buscando en la base de datos...</td></tr>
            ) : estudiantes.map((e) => (
              <tr key={e.id} className="hover:bg-gray-50">
                <td className="p-4 text-sm font-mono">{e.documento_estudiante}</td>
                <td className="p-4 text-sm font-medium">{e.nombres_estudiante} {e.apellidos_estudiante}</td>
                <td className="p-4 text-sm">{e.grado}°{e.grupo}</td>
                <td className="p-4 text-sm text-gray-500">{e.sede_nombre || "Principal"}</td>
                <td className="p-4 text-sm">
                  <span className={`px-2 py-1 rounded-full text-xs font-bold ${e.estado === 'activo' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {e.estado}
                  </span>
                </td>
                <td className="p-4 text-center">
                  <button onClick={() => abrirFicha(e.id)} className="text-blue-600 hover:bg-blue-50 px-3 py-1 rounded-lg font-bold">
                    Ver Ficha
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* PAGINACIÓN */}
      <div className="flex justify-center items-center gap-4 mt-6">
        <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="p-2 border rounded-lg bg-white disabled:opacity-50">◀ Anterior</button>
        <span className="font-bold text-gray-600">Página {page} de {totalPages}</span>
        <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="p-2 border rounded-lg bg-white disabled:opacity-50">Siguiente ▶</button>
      </div>

      {/* MODAL DE FICHA */}
      {mostrarFicha && (
        <div className="modal-overlay fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="modal-card bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center bg-gray-50">
              <h3 className="text-xl font-bold">📄 Expediente del Estudiante</h3>
              <button onClick={() => setMostrarFicha(false)} className="text-2xl hover:text-red-500">×</button>
            </div>

            <div className="p-6">
              {loadingFicha ? <p className="text-center p-10">Cargando datos...</p> : ficha && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Campos Editables */}
                  {[
                    { label: "Nombres", name: "nombres_estudiante" },
                    { label: "Apellidos", name: "apellidos_estudiante" },
                    { label: "Documento", name: "documento_estudiante" },
                    { label: "Teléfono", name: "telefono_estudiante" },
                    { label: "Email", name: "email_estudiante" }
                  ].map(campo => (
                    <div key={campo.name}>
                      <label className="text-xs font-bold text-gray-400 block mb-1">{campo.label}</label>
                      {modoEdicion ? (
                        <input
                          className="w-full p-2 border rounded bg-blue-50"
                          value={ficha[campo.name] || ""}
                          onChange={(e) => setFicha({...ficha, [campo.name]: e.target.value})}
                        />
                      ) : <p className="font-medium">{ficha[campo.name] || "—"}</p>}
                    </div>
                  ))}
                  <div>
                    <label className="text-xs font-bold text-gray-400 block mb-1">Estado</label>
                    {modoEdicion ? (
                      <select 
                        className="w-full p-2 border rounded bg-blue-50"
                        value={ficha.estado}
                        onChange={(e) => setFicha({...ficha, estado: e.target.value})}
                      >
                        <option value="activo">Activo</option>
                        <option value="retirado">Retirado</option>
                      </select>
                    ) : <p className="font-bold text-blue-600 uppercase">{ficha.estado}</p>}
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t bg-gray-50 flex flex-wrap gap-2 justify-end">
              {!modoEdicion ? (
                <>
                  <button onClick={() => setModoEdicion(true)} className="bg-amber-500 text-white px-4 py-2 rounded-lg font-bold">✏️ Editar</button>
                  <button onClick={imprimirPDF} className="bg-gray-800 text-white px-4 py-2 rounded-lg font-bold">🖨️ PDF</button>
                </>
              ) : (
                <button onClick={guardarFicha} disabled={guardando} className="bg-green-600 text-white px-6 py-2 rounded-lg font-bold">
                  {guardando ? "Guardando..." : "💾 Guardar Cambios"}
                </button>
              )}
              <button onClick={() => setMostrarFicha(false)} className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-bold">Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}